/**
 * Product-details and mapping editor inside the Signify product modal.
 * One read-only table lists what configuration filled — Record Name,
 * constants, response paths — and one what the integration fixes; both share a
 * Source / Value / Destination column set. Record Name always renders, naming
 * its refusal rather than disappearing. Path rows are keyed by response path
 * (`product-`, `price-`, `top-`), the key a session override writes, and alone
 * carry `fieldId` for validation. A destination `aria-label` names its table and
 * its source, which is what keeps two rows sharing one destination distinct.
 */
import { FC, ReactNode, useMemo, useContext } from 'react';
import {
  Table,
  Typography,
  Collapse,
  CollapseProps,
  Tag,
  Alert,
  Select,
  Tooltip,
  Input,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ProductSearchResult,
  ProductAndPriceAndAvailabilityResponse,
  formatFieldName,
  flattenApiObject,
  SIGNIFY_PRODUCT_PRIORITY_FIELDS,
} from '#types/SignifyApi.types';
import type { SignifyValueSource } from '#types';
import type { SignifyConstantMapping, SignifySourceRule } from '../../../resolveSignifySourceMappings';
import type { SignifyRecordName } from '../use/services';
import { formatSignifySourceLabel } from '../../../formatSignifySourceLabel';
import { FieldTypeContext } from '../../../../../providers/FieldTypeContext';
import FlexBox from '../../../../../components/atoms/FlexBox';
import FlexCol from '../../../../../components/atoms/FlexCol';
import PricingStatusTag from './PricingStatusTag';
import { REQUIRED_SOURCE_ID_MAPPING } from '#types';

const { Text } = Typography;
const { useToken } = theme;

const describeSource = (source: SignifyValueSource): string =>
  source.type === 'constant' ? `"${String(source.value)}"` : formatSignifySourceLabel(source.path);

/**
 * The "why did this row get this destination" note: the condition that selected
 * the rule and the fallback list it offered. A default rule with a single source
 * explains nothing, so it returns an empty string.
 */
const describeSignifyRule = (rule: SignifySourceRule | undefined): string => {
  if (!rule || (!rule.when && rule.sources.length < 2)) return '';
  const condition = rule.when
    ? `when ${rule.when.path} is ${rule.when.equals.join(', ')}`
    : 'by default';
  return ` — resolved ${condition} from ${rule.sources.map(describeSource).join(' → ')}`;
};

/** What one row will write and where; `problem` says why it cannot. */
type SignifyMappingRow = {
  key: string;
  source: string;
  value: string;
  destination: string;
  problem?: string;
};

/** A row a response path backs: validated here and re-pointable in Edit Mappings. */
type SignifyPathMappingRow = SignifyMappingRow & {
  path: string;
  fieldId: string;
  rawValue: unknown;
};

type SignifyConfiguredMappingRow = SignifyMappingRow | SignifyPathMappingRow;

/** The text an object-shaped API value presents, before any per-row formatting. */
const describeObjectValue = (value: Record<string, unknown>): string => {
  const preferred = [value.formattedValue, value.displayValue, value.label].find((candidate) =>
    Boolean(candidate)
  );
  return preferred === undefined ? JSON.stringify(value) : String(preferred);
};

/**
 * The Record Name row: the source that named the product, the name itself, and
 * the refusal the insert will raise when the rules produce none. The row is
 * rendered either way — a product that cannot be named is the state an operator
 * most needs to see.
 */
const describeSignifyRecordName = (
  recordNameInfo: SignifyRecordName | null
): { source: string; value: string; problem?: string } => {
  if (!recordNameInfo) return { source: '', value: '' };
  if (recordNameInfo.status === 'resolved') {
    return { source: describeSource(recordNameInfo.source), value: recordNameInfo.name };
  }
  if (recordNameInfo.status === 'notText') {
    return {
      source: describeSource(recordNameInfo.source),
      value: recordNameInfo.valueSummary,
      problem: `Rule ${recordNameInfo.rulePosition} of the Record Name destination matched and `
        + `produced ${recordNameInfo.valueSummary}, which is blank or not text. `
        + 'Insert is refused until that rule reads a text source.'
    };
  }
  return {
    source: '',
    value: 'No value',
    problem: 'No Record Name rule produced a value from this product. '
      + 'Insert is refused until a rule matches it.'
  };
};

/**
 * Props for SignifyProductDetailsWithMapping component
 */
export interface SignifyProductDetailsWithMappingProps {
  selectedProduct: ProductSearchResult | null;
  productPriceAndAvailabilityData: ProductAndPriceAndAvailabilityResponse | null;
  /** Response path -> the destination the org's rules resolved from it. */
  sourceMappings: Record<string, string>;
  /** Response path -> the rule that supplied its destination. */
  sourceRules: Record<string, SignifySourceRule>;
  /** Destinations a constant fills; no response row backs them. */
  constantMappings: SignifyConstantMapping[];
  customMappings: Record<string, string | null>;
  availableTableColumns: string[];
  duplicateMappings: Record<string, string[]>;
  validateSingleMapping: (apiField: string, lineItemField: string) => { isValid: boolean; errorMessage: string | null };
  sourceTypesMap: Map<string, any>;
  destinationFieldsMap: Map<string, any>;
  /** The name this product resolves to, or which way the rules failed to name it. */
  recordNameInfo: SignifyRecordName | null;
  /** The entity label every destination writes to; the note is dropped when absent. */
  destinationEntityLabel?: string;
  getColumnDisplayName: (columnId: string) => string;
  onMappingChange: (apiField: string, tableColumn: string | null) => void;
}

/**
 * Component displaying product details with field mapping functionality
 * Shows both mapped fields table and collapsible detailed view
 */
const SignifyProductDetailsWithMapping: FC<SignifyProductDetailsWithMappingProps> = ({
  selectedProduct,
  productPriceAndAvailabilityData,
  sourceMappings,
  sourceRules,
  constantMappings,
  customMappings,
  availableTableColumns,
  duplicateMappings,
  validateSingleMapping,
  sourceTypesMap,
  destinationFieldsMap,
  recordNameInfo,
  destinationEntityLabel,
  getColumnDisplayName,
  onMappingChange,
}) => {
  const { token } = useToken();
  const { fieldTypes } = useContext(FieldTypeContext);

  /**
   * Everything configuration fills, in reading order: the record's own name,
   * the destinations a constant supplies, then one row per response path the
   * item actually returned.
   */
  const configuredMappingRows = useMemo<SignifyConfiguredMappingRow[]>(() => {
    if (!selectedProduct) return [];

    const recordNameRow: SignifyMappingRow = {
      key: 'recordName',
      ...describeSignifyRecordName(recordNameInfo),
      destination: 'Record Name',
    };

    const constantRows: SignifyMappingRow[] = constantMappings.map(({ fieldId, value }) => ({
      key: `constant-${fieldId}`,
      source: 'Constant',
      value: String(value),
      destination: getColumnDisplayName(fieldId),
    }));

    const allMappings: Record<string, string | null> = {
      ...sourceMappings,
      ...customMappings,
    };

    const productData = productPriceAndAvailabilityData?.product || selectedProduct;

    const productRows = Object.entries(allMappings).flatMap<SignifyPathMappingRow>(
      ([path, fieldId]) => {
        if (!fieldId || fieldId === 'null' || !path.startsWith('product.')) return [];

        const value = path
          .replace('product.', '')
          .split('.')
          .reduce<unknown>((obj, key) => {
            if (obj && typeof obj === 'object' && key in obj) {
              return (obj as Record<string, unknown>)[key];
            }
            return undefined;
          }, productData as unknown);

        if (value === null || value === undefined || value === '') return [];

        const displayValue =
          typeof value === 'object' && value !== null
            ? (() => {
                const record = value as Record<string, unknown>;
                if ('formattedValue' in record) return record.formattedValue;
                if ('displayValue' in record) return record.displayValue;
                if ('label' in record) return record.label;
                if ('name' in record) return record.name;
                return value;
              })()
            : value;

        return [{
          key: `product-${path}`,
          source: formatSignifySourceLabel(path),
          value: displayValue === null || displayValue === undefined ? '' : String(displayValue),
          destination: getColumnDisplayName(fieldId),
          path,
          fieldId,
          rawValue: value,
        }];
      }
    );

    const priceRows = (productPriceAndAvailabilityData?.price?.prices ?? []).flatMap<SignifyPathMappingRow>(
      (price) => {
        const path = `price.prices.${price.priceTypeIdentifier}`;
        const fieldId = allMappings[path];
        if (!fieldId || fieldId === 'null') return [];

        return [{
          key: `price-${price.priceTypeIdentifier}`,
          source: formatSignifySourceLabel(path),
          value: String(price.formattedValue),
          destination: getColumnDisplayName(fieldId),
          path,
          fieldId,
          rawValue: price,
        }];
      }
    );

    const STRUCTURAL_KEYS = new Set(['product', 'price', 'availability', 'priceError', 'availabilityError']);
    const response = productPriceAndAvailabilityData as unknown as Record<string, unknown> | null;

    const topLevelRows = !response
      ? []
      : Object.entries(allMappings).flatMap<SignifyPathMappingRow>(([path, fieldId]) => {
          if (
            !fieldId ||
            fieldId === 'null' ||
            path.startsWith('product.') ||
            STRUCTURAL_KEYS.has(path) ||
            !(path in response)
          ) return [];

          const value = response[path];
          const isScalar = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
          if (!isScalar || value === '') return [];

          return [{
            key: `top-${path}`,
            source: formatSignifySourceLabel(path),
            value: String(value),
            destination: getColumnDisplayName(fieldId),
            path,
            fieldId,
            rawValue: value,
          }];
        });

    return [recordNameRow, ...constantRows, ...productRows, ...priceRows, ...topLevelRows];
  }, [
    selectedProduct,
    productPriceAndAvailabilityData,
    sourceMappings,
    customMappings,
    constantMappings,
    recordNameInfo,
    getColumnDisplayName,
  ]);

  /**
   * Destinations the integration owns. Manufacturer Name belongs here because
   * the destination is fixed: only the brand-code lookup behind it is
   * configured, on its own settings card.
   */
  const fixedMappingRows = useMemo<SignifyMappingRow[]>(() => {
    if (!selectedProduct) return [];

    const brandCode = productPriceAndAvailabilityData?.product?.brand?.code || selectedProduct.brand?.code;

    return [
      {
        key: 'manufacturersSourceId',
        source: 'Constant',
        value: REQUIRED_SOURCE_ID_MAPPING,
        destination: 'Manufacturers Source ID',
      },
      ...(selectedProduct.code
        ? [{
            key: 'manufacturersProductId',
            source: 'Product Code',
            value: selectedProduct.code,
            destination: 'Manufacturers Product ID',
          }]
        : []),
      ...(brandCode
        ? [{
            key: 'manufacturerEntity',
            source: 'Brand Code',
            value: brandCode,
            destination: 'Manufacturer Name',
          }]
        : []),
    ];
  }, [selectedProduct, productPriceAndAvailabilityData]);

  /**
   * One column set for both read-only tables. `destinationKind` is the only
   * difference: it prefixes the destination `aria-label`, which the row's own
   * source completes. Only Record Name can lack a source, and only when no rule
   * named the product, so the sourceless label it falls back to stays unique.
   */
  const mappingColumns = (
    destinationKind: 'Configured' | 'Fixed'
  ): ColumnsType<SignifyConfiguredMappingRow> => [
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '30%',
      render: (_: unknown, record: SignifyConfiguredMappingRow) =>
        'path' in record ? (
          <Tooltip title={`Type: ${sourceTypesMap.get(record.path)?.inferredType || 'unknown'}`}>
            <Text>{record.source}</Text>
          </Tooltip>
        ) : (
          <Text>{record.source}</Text>
        ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (_: unknown, record: SignifyConfiguredMappingRow) => {
        if (record.problem) {
          return (
            <Tooltip title={record.problem}>
              <Text type="danger">{record.value}</Text>
            </Tooltip>
          );
        }

        if (!('path' in record) || record.value === '') return <Text>{record.value}</Text>;

        const isObject = typeof record.rawValue === 'object' && record.rawValue !== null;
        const rawText = isObject
          ? describeObjectValue(record.rawValue as Record<string, unknown>)
          : String(record.rawValue);
        if (rawText === record.value) return <Text>{record.value}</Text>;

        const tooltipContent: ReactNode = isObject ? (
          <FlexCol gap={0}>
            <Text strong>From API (object):</Text>
            <Text code style={{ fontSize: token.fontSizeSM, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(record.rawValue, null, 2)}
            </Text>
            <Text>
              <Text strong>Will Save: </Text>
              {record.value}
            </Text>
          </FlexCol>
        ) : (
          <FlexCol gap={0}>
            <Text>
              <Text strong>From API: </Text>
              {rawText}
            </Text>
            <Text>
              <Text strong>Will Save: </Text>
              {record.value}
            </Text>
          </FlexCol>
        );

        return (
          <Tooltip title={tooltipContent}>
            <Text>{record.value}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
      width: '40%',
      render: (_: unknown, record: SignifyConfiguredMappingRow) => {
        const isPathRow = 'path' in record;
        const duplicate = isPathRow ? duplicateMappings[record.fieldId] : undefined;
        const validation = isPathRow
          ? validateSingleMapping(record.path, record.fieldId)
          : { isValid: true, errorMessage: null };
        const isValid = validation.isValid && !duplicate && !record.problem;

        const destinationType = isPathRow
          ? destinationFieldsMap.get(record.fieldId)?.fieldDataTypeId
          : undefined;

        const tooltipTitle = duplicate
          ? `Multiple sources mapped to this destination: ${duplicate.map((f) => formatSignifySourceLabel(f)).join(', ')}`
          : !validation.isValid
            ? validation.errorMessage || ''
            : record.problem || (destinationType ? `Type: ${destinationType}` : '');

        return (
          <Tooltip title={tooltipTitle || undefined}>
            <Input
              variant="borderless"
              aria-label={
                record.source
                  ? `${destinationKind} destination ${record.destination} from ${record.source}`
                  : `${destinationKind} destination ${record.destination}`
              }
              value={record.destination}
              disabled={true}
              status={isValid ? undefined : 'error'}
              style={{
                width: '100%',
                backgroundColor: token.colorFillTertiary,
                cursor: isValid ? 'default' : 'help',
                border: isValid
                  ? undefined
                  : `${token.lineWidthBold}px ${token.lineType} ${token.colorError}`,
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  if (!selectedProduct) return null;

  const productEntries = flattenApiObject(
    selectedProduct as unknown as Record<string, unknown>
  ).sort(([a], [b]) => {
    const aIndex = SIGNIFY_PRODUCT_PRIORITY_FIELDS.findIndex(
      (field: string) => field === a
    );
    const bIndex = SIGNIFY_PRODUCT_PRIORITY_FIELDS.findIndex(
      (field: string) => field === b
    );
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  const productDetailsData = productEntries
    .map(([key, value]: [string, unknown]) => {
      if (!value && value !== 0 && value !== false) return null;

      let displayValue: any = value;
      if (typeof value === 'object' && value !== null) {
        if ('name' in value) {
          displayValue = (value as any).name;
        } else {
          displayValue = JSON.stringify(value);
        }
      }

      return {
        apiField: `product.${key}`,
        fieldName: formatFieldName(`product.${key}`),
        value: displayValue,
        tableColumn: null,
        key: `detail-${key}`,
      };
    })
    .filter(Boolean);

  const priceDetailsData =
    productPriceAndAvailabilityData?.price?.prices?.map((price) => {
      return {
        apiField: `price.prices.${price.priceTypeIdentifier}`,
        fieldName: formatSignifySourceLabel(price.priceTypeIdentifier),
        value: price.formattedValue,
        tableColumn: null,
        key: `price-detail-${price.priceTypeIdentifier}`,
      };
    }) || [];

  const allMappings: Record<string, string | null> = {
    ...sourceMappings,
    ...customMappings,
  };
  const allDetailsData = [...productDetailsData, ...priceDetailsData]
    .map((item) => {
      if (!item) return null;
      return {
        ...item,
        tableColumn:
          allMappings[item.apiField] ||
          customMappings[item.apiField] ||
          null,
      };
    })
    .filter(Boolean) as Array<{
      apiField: string;
      fieldName: string;
      value: React.ReactNode;
      tableColumn: string | null;
      key: string;
    }>;

  const mappedColumns = new Set(
    Object.values(allMappings).filter((v) => v && v !== 'null')
  );
  const availableForMapping = availableTableColumns.filter(
    (col) => !mappedColumns.has(col)
  );

  const detailsTableColumns = [
    {
      title: 'Source',
      dataIndex: 'fieldName',
      key: 'fieldName',
      width: '30%',
      render: (text: string, record: any) => {
        const sourceInfo = sourceTypesMap.get(record.apiField);
        const inferredType = sourceInfo?.inferredType || 'unknown';

        return (
          <Tooltip title={`Type: ${inferredType}`}>
            <Text>{text}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (text: any, record: any) => {
        const originalMapping = sourceMappings[record.apiField];
        const currentMapping =
          customMappings[record.apiField] !== undefined
            ? customMappings[record.apiField]
            : originalMapping;

        if (!currentMapping) {
          return (
            <Text strong={record.apiField === 'product.name'}>
              {text}
            </Text>
          );
        }

        const targetFieldType = fieldTypes[currentMapping];
        if (!targetFieldType) {
          return <Text>{text}</Text>;
        }

        let willSave = text;
        const isTextField = ['text', 'shortText', 'longText'].includes(targetFieldType.fieldDataTypeId);
        const isDateField = ['date', 'dateTime'].includes(targetFieldType.fieldDataTypeId);

        const rawValue = text;
        const rawValueStr = String(rawValue);

        if (isTextField && typeof rawValue === 'object' && rawValue !== null) {
          willSave = (rawValue as any).formattedValue
            || (rawValue as any).displayValue
            || (rawValue as any).label
            || String(rawValue);
        } else if (isDateField && typeof rawValue === 'string') {
          const datePatterns = [
            /\b(\d{1,2}\/\d{1,2}\/\d{4})\b/,
            /\b(\d{4}-\d{2}-\d{2})\b/,
            /\b(\d{1,2}-\d{1,2}-\d{4})\b/,
          ];
          for (const pattern of datePatterns) {
            const match = rawValue.match(pattern);
            if (match) {
              willSave = match[1];
              break;
            }
          }
        }

        willSave = String(willSave);

        if (rawValueStr === willSave) {
          return (
            <Text strong={record.apiField === 'product.name'}>
              {text}
            </Text>
          );
        }

        const tooltipContent = (
          <div>
            <div><strong>From API:</strong> {rawValueStr}</div>
            <div><strong>Will Save:</strong> {willSave}</div>
          </div>
        );

        return (
          <Tooltip title={tooltipContent}>
            <Text strong={record.apiField === 'product.name'}>
              {text}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Destination',
      dataIndex: 'tableColumn',
      key: 'tableColumn',
      width: '40%',
      render: (text: string, record: any) => {
        const originalMapping = sourceMappings[record.apiField];
        const currentMapping =
          customMappings[record.apiField] !== undefined
            ? customMappings[record.apiField]
            : text || originalMapping;

        const optionsToShow =
          currentMapping &&
          !availableForMapping.includes(currentMapping)
            ? [currentMapping, ...availableForMapping]
            : availableForMapping;

        const validation = currentMapping
          ? validateSingleMapping(record.apiField, currentMapping)
          : { isValid: true, errorMessage: null };

        const isDuplicate = currentMapping ? duplicateMappings[currentMapping] : false;
        const isValid = validation.isValid && !isDuplicate;

        // Get source and destination types for tooltip
        const sourceInfo = sourceTypesMap.get(record.apiField);
        const sourceType = sourceInfo?.inferredType || 'unknown';
        const destType = currentMapping
          ? destinationFieldsMap.get(currentMapping)?.fieldDataTypeId || ''
          : '';
        const ruleNote = describeSignifyRule(sourceRules[record.apiField]);

        let tooltipMessage = '';
        if (isDuplicate) {
          tooltipMessage = `Multiple sources mapped to this destination: ${isDuplicate.map(f => formatSignifySourceLabel(f)).join(', ')}`;
        } else if (!validation.isValid) {
          tooltipMessage = validation.errorMessage || '';
        } else if (currentMapping) {
          tooltipMessage = `Source type: ${sourceType} → Destination type: ${destType}${ruleNote}`;
        }

        const borderColor = !isValid ? token.colorError : undefined;

        return (
          <Tooltip title={tooltipMessage || undefined}>
            <Select
              variant="borderless"
              style={{
                width: '250px',
                backgroundColor: token.colorFillTertiary,
                border: borderColor ? `2px solid ${borderColor}` : undefined,
              }}
              placeholder="Select destination"
              value={currentMapping || undefined}
              onChange={(value) =>
                onMappingChange(record.apiField, value)
              }
              allowClear
              size="small"
              optionLabelProp="label"
              showSearch
              filterOption={(input, option) => {
                const label = (option?.label as string) || '';
                const value = (option?.value as string) || '';
                const searchText = input.toLowerCase();
                return (
                  label.toLowerCase().includes(searchText) ||
                  value.toLowerCase().includes(searchText)
                );
              }}
            >
              {optionsToShow.map((col) => (
                <Select.Option
                  key={col}
                  value={col}
                  label={getColumnDisplayName(col)}
                >
                  {getColumnDisplayName(col)}
                </Select.Option>
              ))}
            </Select>
          </Tooltip>
        );
      },
    },
  ];

  const collapseItems: CollapseProps['items'] = [
    {
      key: 'allDetails',
      label: (
        <FlexBox gap={0}>
          <Text strong>Edit Mappings</Text>
          <FlexBox gap={0} justifyEnd noGrow>
            {selectedProduct.onlinePurchasable && (
              <Tag color="success">Available Online</Tag>
            )}
            {selectedProduct.configurable && (
              <Tag color="blue">Configurable</Tag>
            )}
            {selectedProduct.delistmentDate && (
              <Tag
                color={
                  new Date(selectedProduct.delistmentDate) <
                  new Date()
                    ? 'error'
                    : 'warning'
                }
              >
                {new Date(selectedProduct.delistmentDate) < new Date()
                  ? 'Product Delisted'
                  : 'Delisting Soon'}
              </Tag>
            )}
            <PricingStatusTag productPriceAndAvailabilityData={productPriceAndAvailabilityData} />
          </FlexBox>
        </FlexBox>
      ),
      children: (
        <>
          {productPriceAndAvailabilityData?.priceError ? (
            <Alert
              title="Price Not Available"
              description={productPriceAndAvailabilityData.priceError}
              type="warning"
              showIcon
              style={{ width: '100%', marginBottom: 16 }}
            />
          ) : null}
          {productPriceAndAvailabilityData?.availabilityError ? (
            <Alert
              title="Availability Not Available"
              description={`${productPriceAndAvailabilityData.availabilityError} — Lead Times will not be populated on the inserted row.`}
              type="warning"
              showIcon
              style={{ width: '100%', marginBottom: 16 }}
            />
          ) : null}
          <Table
            dataSource={allDetailsData}
            columns={detailsTableColumns}
            pagination={false}
            size="small"
            rowKey="key"
            tableLayout="fixed"
          />
        </>
      ),
    },
  ];

  return (
    <>
      <FlexCol gap={0}>
        <Text>Configured field mappings</Text>
        {destinationEntityLabel ? (
          <Text type="secondary">{`Destinations are fields on the ${destinationEntityLabel} record.`}</Text>
        ) : null}
        <Table<SignifyConfiguredMappingRow>
          dataSource={configuredMappingRows}
          columns={mappingColumns('Configured')}
          pagination={false}
          size="small"
          rowKey="key"
          tableLayout="fixed"
        />
      </FlexCol>

      <FlexCol gap={0}>
        <Text>Fixed by the integration</Text>
        <Table<SignifyConfiguredMappingRow>
          dataSource={fixedMappingRows}
          columns={mappingColumns('Fixed')}
          pagination={false}
          size="small"
          rowKey="key"
          tableLayout="fixed"
        />
      </FlexCol>
      <Collapse
        items={collapseItems}
        defaultActiveKey={['allDetails']}
        style={{ width: '100%' }}
        className="no-padding-collapse"
      />
    </>
  );
};

export default SignifyProductDetailsWithMapping;
